/**
 * useObjectInspectorGraphQL Hook
 *
 * Apollo Client hook for fetching object details by global ID.
 * Replaces the REST-based useObjectInspector hook for the admin object browser.
 *
 * Feature: 062-graphql-api
 */

import { useQuery } from '@apollo/client';
import { decodeGlobalId } from '@206mp/shared/utils/globalObjectId';
import { OBJECT_QUERIES, RESULT_FIELD_NAMES } from '../graphql/queries/objects';
import logger from '../utils/logger';

export interface ObjectInspectorGraphQLResponse {
  globalId: string;
  type: string;
  id: string;
  object: Record<string, any>;
}

/**
 * Custom hook to fetch any object by global ID using GraphQL
 */
export function useObjectInspectorGraphQL(globalId: string | undefined) {
  // Decode the global ID to determine the object type
  const decodedId = globalId ? decodeGlobalId(globalId) : null;
  const objectType = decodedId && !('error' in decodedId) ? decodedId.type : null;
  const numericId = decodedId && !('error' in decodedId) ? decodedId.id : null;

  // Get the appropriate query for this object type
  const query = objectType ? OBJECT_QUERIES[objectType] : null;
  const resultFieldName = objectType ? RESULT_FIELD_NAMES[objectType] : null;

  // Execute query
  const { data, loading, error } = useQuery(query || OBJECT_QUERIES.users, {
    variables: { id: globalId },
    skip: !globalId || !query || !objectType,
    onError: (err) => {
      logger.error('GraphQL', 'Object inspector query failed', {
        globalId,
        objectType,
        error: err.message,
      });
    },
  });

  // Transform the GraphQL response to match the expected format
  let transformedData: ObjectInspectorGraphQLResponse | undefined;

  if (data && resultFieldName && data[resultFieldName]) {
    const objectData = data[resultFieldName];

    // Extract the object fields (excluding __typename)
    const cleanObject: Record<string, any> = {};
    for (const [key, value] of Object.entries(objectData)) {
      if (key !== '__typename') {
        cleanObject[key] = value;
      }
    }

    transformedData = {
      globalId: objectData.globalId || objectData.id,
      type: objectType || '',
      id: String(numericId),
      object: cleanObject,
    };

    logger.debug('GraphQL', 'Object inspector query succeeded', {
      globalId,
      objectType,
      hasData: true,
    });
  }

  // Transform Apollo error to match React Query error interface
  const transformedError = error ? new Error(
    error.graphQLErrors?.[0]?.message ||
    error.networkError?.message ||
    error.message ||
    'Unknown GraphQL error'
  ) : null;

  return {
    data: transformedData,
    isLoading: loading,
    error: transformedError,
  };
}

/**
 * GraphQL-based hook for fetching object relationships.
 *
 * Note: The GraphQL schema exposes relationships as nested fields on each object type.
 * For the object inspector, we fetch relationships by examining the object's nested data.
 * This is simpler than the REST approach which required a separate endpoint.
 */
export function useObjectRelationshipsGraphQL(globalId: string | undefined) {
  // For GraphQL, relationships are fetched as part of the main query
  // The object inspector shows nested relationships from the object data itself
  // This hook maintains API compatibility but returns empty relationships
  // since the main query already includes relationship data

  return {
    data: {
      globalId: globalId || '',
      type: '',
      incomingRelationships: [],
    },
    isLoading: false,
    error: null,
  };
}
